/**
 * Module-level pub/sub store that tracks the current bulk Xero push.
 *
 * `bulkInvoiceService.bulkPushToXero` and the Xero-syncing path of
 * `bulkSendEmails` write into this store on every progress tick. The
 * `XeroPushProgressToast` mounted in PlatformLayout subscribes to it and
 * renders a fixed bottom-right card while a push is running, so the user can
 * keep working / navigate away without losing the indicator.
 *
 * Tiny implementation — no Zustand dependency. If we end up needing this
 * pattern more than 2-3 times across the platform we can promote it.
 */

import type { BulkOperationProgress } from '@/types/bulkOperations';

export type XeroPushProgress = BulkOperationProgress | null;

type Listener = (progress: XeroPushProgress) => void;

let current: XeroPushProgress = null;
const listeners = new Set<Listener>();

function emit() {
  for (const listener of listeners) {
    try {
      listener(current);
    } catch (e) {
      // Subscriber failure shouldn't break the queue
      console.error('[xeroPushProgressStore] listener error', e);
    }
  }
}

export const xeroPushProgressStore = {
  /** Replace the current progress snapshot and notify subscribers. */
  set(progress: XeroPushProgress) {
    current = progress;
    emit();
  },

  /** Clear the active progress (e.g. after a 5s grace period when complete). */
  clear() {
    current = null;
    emit();
  },

  /** Read the current snapshot synchronously. */
  get(): XeroPushProgress {
    return current;
  },

  /** Subscribe to changes. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
